import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, storage } from "./firebase";
import { collection, doc, setDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import "./CreateTournament.css"; // Import styles
import useGameCatalog from "./hooks/useGameCatalog";

const CreateTournament = () => {
  const navigate = useNavigate();
  const { activeGames, gameMap, loading: gamesLoading } = useGameCatalog();
  const [tournamentName, setTournamentName] = useState("");
  const [gameId, setGameId] = useState("");
  const [prizePool, setPrizePool] = useState(0);
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [registrationEndsOn, setRegistrationEndsOn] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [checkInTime, setCheckInTime] = useState("");
  const [type, setType] = useState("Team");
  const [minTeamSize, setMinTeamSize] = useState(5);
  const [maxRegistrations, setMaxRegistrations] = useState(100); // New Field
  const [prizeDistribution, setPrizeDistribution] = useState([]);
  const [placementPoints, setPlacementPoints] = useState([]);

  const [bannerFile, setBannerFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [bannerURL, setBannerURL] = useState("");
  const [imageURL, setImageURL] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (!activeGames.length) return;
    setGameId((current) => {
      if (current && activeGames.some((game) => game.id === current)) {
        return current;
      }
      return activeGames[0].id;
    });
  }, [activeGames]);

  const handleUpload = (file, setURL) => {
    if (!file) return;

    const storageRef = ref(storage, `tournament-images/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => console.error("Upload error:", error),
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        setURL(downloadURL);
      }
    );
  };

  const handleAddPrize = () => {
    setPrizeDistribution([...prizeDistribution, { position: "", amount: "" }]);
  };

  const handlePrizeChange = (index, field, value) => {
    const updatedPrizes = [...prizeDistribution];
    updatedPrizes[index][field] = value;
    setPrizeDistribution(updatedPrizes);
  };

  const handleRemovePrize = (index) => {
    const updatedPrizes = [...prizeDistribution];
    updatedPrizes.splice(index, 1);
    setPrizeDistribution(updatedPrizes);
  };

  const handleAddPlacementPoint = () => {
    setPlacementPoints([
      ...placementPoints,
      { position: "", points: "" },
    ]);
  };

  const handlePlacementPointChange = (index, field, value) => {
    const updated = [...placementPoints];
    updated[index][field] = value;
    setPlacementPoints(updated);
  };

  const handleRemovePlacementPoint = (index) => {
    const updated = [...placementPoints];
    updated.splice(index, 1);
    setPlacementPoints(updated);
  };

  const handleCreateTournament = async () => {
    if (
      !tournamentName ||
      !gameId ||
      prizePool <= 0 ||
      !startsOn ||
      !endsOn ||
      !registrationEndsOn ||
      maxRegistrations <= 0
    ) {
      alert("Please fill all required fields!");
      return;
    }

    const tournamentRef = doc(collection(db, "tournaments"));
    await setDoc(tournamentRef, {
      tournamentName,
      gameId,
      prizePool: Number(prizePool),
      startsOn,
      endsOn,
      registrationEndsOn,
      shortDescription,
      checkInTime,
      type,
      registered: 0,
      minTeamSize: Number(minTeamSize),
      maxRegistrations: Number(maxRegistrations), // Save to Firestore
      prizeDistribution,
      placementPoints:
        gameMap[gameId]?.format === "battle_royale"
          ? placementPoints
              .map((point) => ({
                position: Number(point.position),
                points: Number(point.points),
              }))
              .filter(
                (point) =>
                  !Number.isNaN(point.position) && !Number.isNaN(point.points)
              )
          : [],
      tournamentBanner: bannerURL,
      tournamentImage: imageURL,
    });

    alert("Tournament Created!");
    navigate("/tournaments");
  };

  const isBattleRoyale = gameMap[gameId]?.format === "battle_royale";

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Create tournament</h1>
          <p>Set up schedules, rewards, and registration rules.</p>
        </div>
      </div>

      <div className="card form-card">
        <div className="form-grid">
          <label className="field">
            <span>Tournament Name</span>
            <input
              type="text"
              value={tournamentName}
              onChange={(e) => setTournamentName(e.target.value)}
            />
          </label>

          <label className="field">
            <span>Game</span>
            <select
              value={gameId}
              onChange={(e) => setGameId(e.target.value)}
              disabled={gamesLoading || !activeGames.length}
            >
              {activeGames.map((game) => (
                <option key={game.id} value={game.id}>
                  {game.name}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Prize Pool (INR)</span>
            <input
              type="number"
              value={prizePool}
              onChange={(e) => setPrizePool(e.target.value)}
            />
          </label>

          <label className="field">
            <span>Max Registrations</span>
            <input
              type="number"
              value={maxRegistrations}
              onChange={(e) => setMaxRegistrations(e.target.value)}
            />
          </label>

          <label className="field">
            <span>Starts On</span>
            <input
              type="datetime-local"
              value={startsOn}
              onChange={(e) => setStartsOn(e.target.value)}
            />
          </label>

          <label className="field">
            <span>Ends On</span>
            <input
              type="datetime-local"
              value={endsOn}
              onChange={(e) => setEndsOn(e.target.value)}
            />
          </label>

          <label className="field">
            <span>Registration Ends On</span>
            <input
              type="datetime-local"
              value={registrationEndsOn}
              onChange={(e) => setRegistrationEndsOn(e.target.value)}
            />
          </label>

          <label className="field field-wide">
            <span>Short Description</span>
            <textarea
              value={shortDescription}
              onChange={(e) => setShortDescription(e.target.value)}
            />
          </label>

          <label className="field">
            <span>Check-in Time</span>
            <input
              type="text"
              value={checkInTime}
              onChange={(e) => setCheckInTime(e.target.value)}
            />
          </label>

          <label className="field">
            <span>Type</span>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="Team">Team</option>
              <option value="Solo">Solo</option>
            </select>
          </label>

          <label className="field">
            <span>Min Team Size</span>
            <input
              type="number"
              value={minTeamSize}
              onChange={(e) => setMinTeamSize(e.target.value)}
            />
          </label>
        </div>

        <div className="divider" />

        <div className="form-section">
          <div className="section-title">Prize Distribution</div>
          {prizeDistribution.map((prize, index) => (
            <div key={index} className="prize-row">
              <input
                type="number"
                placeholder="Position"
                value={prize.position}
                onChange={(e) =>
                  handlePrizeChange(index, "position", e.target.value)
                }
              />
              <input
                type="number"
                placeholder="Amount (INR)"
                value={prize.amount}
                onChange={(e) =>
                  handlePrizeChange(index, "amount", e.target.value)
                }
              />
              <button
                type="button"
                className="button ghost"
                onClick={() => handleRemovePrize(index)}
              >
                Remove
              </button>
            </div>
          ))}
          <button type="button" onClick={handleAddPrize} className="button ghost">
            Add Prize
          </button>
        </div>

        <div className="divider" />

        {isBattleRoyale && (
          <>
            <div className="form-section">
              <div className="section-title">Placement Points (Battle Royale)</div>
              {placementPoints.map((point, index) => (
                <div key={index} className="prize-row">
                  <input
                    type="number"
                    placeholder="Placement"
                    value={point.position}
                    onChange={(e) =>
                      handlePlacementPointChange(
                        index,
                        "position",
                        e.target.value
                      )
                    }
                  />
                  <input
                    type="number"
                    placeholder="Points"
                    value={point.points}
                    onChange={(e) =>
                      handlePlacementPointChange(
                        index,
                        "points",
                        e.target.value
                      )
                    }
                  />
                  <button
                    type="button"
                    className="button ghost"
                    onClick={() => handleRemovePlacementPoint(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddPlacementPoint}
                className="button ghost"
              >
                Add Placement Rule
              </button>
            </div>

            <div className="divider" />
          </>
        )}

        <div className="form-section">
          <div className="section-title">Media uploads</div>
          <div className="form-grid">
            <label className="field">
              <span>Tournament Banner (Landscape)</span>
              <input
                type="file"
                onChange={(e) => setBannerFile(e.target.files[0])}
              />
            </label>
            <button
              className="button primary"
              type="button"
              onClick={() => handleUpload(bannerFile, setBannerURL)}
            >
              Upload Banner
            </button>

            <label className="field">
              <span>Tournament Image (Portrait)</span>
              <input
                type="file"
                onChange={(e) => setImageFile(e.target.files[0])}
              />
            </label>
            <button
              className="button primary"
              type="button"
              onClick={() => handleUpload(imageFile, setImageURL)}
            >
              Upload Image
            </button>
          </div>

          {uploadProgress > 0 && (
            <progress value={uploadProgress} max="100"></progress>
          )}
        </div>

        <div className="form-actions">
          <button
            className="button ghost"
            type="button"
            onClick={() => navigate("/tournaments")}
          >
            Cancel
          </button>
          <button
            className="button primary"
            type="button"
            onClick={handleCreateTournament}
          >
            Create Tournament
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTournament;
